import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, throwError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { DecryptionService } from './decrypt.service';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  baseUrl = environment.apiUrl;
  baseUrlArbitrators = environment.apiUrlArbitrators;
  collapseSidebarStatus: boolean = false;
  collapseSidebar = false;
  sidenavMode: any = 'side';
  sidenavOpen = true;
  horizontalSideNavMode = 'over';
  horizontalSideNavOpen = false;
  reloadFormWizard: Subject<boolean> = new Subject();

  userNames: BehaviorSubject<boolean> = new BehaviorSubject(false);
  deleteRoleFromGroup: Subject<any> = new Subject();
  responseType: any = environment.encryptedResponse ? 'text' : 'json';

  constructor(
    private matDialog: MatDialog,
    private decryptionService: DecryptionService,
    private http: HttpClient,
  ) {}

  postRequest(path, body, observe?, arbitrators?, noDecrypt?): Observable<any> {
    const url =  arbitrators ? this.baseUrlArbitrators : this.baseUrl;
    return this.http
      .post(`${url}${path}`, body, {
        ...observe,
        responseType: this.getResponseType(noDecrypt),
      })
      .pipe(
        map((event) => {
          return this.handleResponse(event, noDecrypt);
        }),
        catchError((error) => {
          return throwError(this.handleError(error));
        }),
      );
  }

  getResponseType(noDecrypt?) {
    return (environment.encryptedResponse && !noDecrypt)  ? 'text' : 'json'
  }

  getRequest(path, observe?, arbitrators?, noDecrypt?): Observable<any> {
    const url =  arbitrators ? this.baseUrlArbitrators : this.baseUrl;
    return this.http
      .get(`${url}${path}`, {
        ...observe,
        responseType: this.getResponseType(noDecrypt),
      })
      .pipe(
        map((event) => {
          return this.handleResponse(event, noDecrypt);
        }),
        catchError((error) => {
          return throwError(this.handleError(error));
        }),
      );
  }

  getRequestWithBody(path, body?): Observable<any> {
    return this.http
      .request('Get', `${this.baseUrl}${path}`, {
        body,
        responseType: this.responseType,
      })
      .pipe(
        map((event) => {
          return this.handleResponse(event);
        }),
        catchError((error) => {
          return throwError(this.handleError(error));
        }),
      );
  }

  putRequest(path, body, arbitrators?, noDecrypt?): Observable<any> {
    const url =  arbitrators ? this.baseUrlArbitrators : this.baseUrl;
    return this.http
      .put(`${url}${path}`, body, { responseType: this.getResponseType(noDecrypt) as any })
      .pipe(
        map((event) => {
          return this.handleResponse(event, noDecrypt);
        }),
        catchError((error) => {
          return throwError(this.handleError(error));
        }),
      );
  }

  deleteRequest(path, body?, arbitrators?, noDecrypt?): Observable<any> {
    const url =  arbitrators ? this.baseUrlArbitrators : this.baseUrl;
    return this.http
      .delete(`${url}${path}`, {body, responseType: this.getResponseType(noDecrypt) as any })
      .pipe(
        map((event) => {
          return this.handleResponse(event, noDecrypt);
        }),
        catchError((error) => {
          return throwError(this.handleError(error));
        }),
      );
  }

  handleResponse(event, noDecrypt?): Observable<any> {
    // in case response of status code 204 which has no content (event = null) angular consider it as an error and goes in catchError function
    // this way to handle it and prevent it from going to catchError function
    if (!event) {
      return of(null);
    }
    return (environment.encryptedResponse && !noDecrypt) ? this.decryptResponse(event) : event;
  }

  handleError(error): any {
    // Handle the error here
    console.error('An error occurred:', error);
    // Optionally, you can transform the error or rethrow it
    error.error = environment.encryptedResponse
      ? this.decryptResponse(error.error)
      : error.error;
    return error;
  }

  setUserNames(value) {
    this.userNames.next(value);
  }

  deleteRole(obj) {
    this.deleteRoleFromGroup.next(obj);
  }

  reloadWizard() {
    this.reloadFormWizard.next(true);
  }

  private validateJson(data: string): boolean {
    try {
      JSON.parse(data);
      return true;
    } catch (error) {
      console.error('Invalid JSON format', error);
      return false;
      // throw new Error('Invalid JSON format');
    }
  }

  public decryptResponse(event) {
    var body = event.body ? event.body : event;
    try {
      const decryptedBody = this.decryptionService.decrypt(body);
      if (decryptedBody) {
        // receiving a JSON response even if it json direct body response or HTTPResponse like that {header, body, url}
        if (this.validateJson(decryptedBody)) {
          return event.body
            ? event.clone({ body: JSON.parse(decryptedBody) })
            : JSON.parse(decryptedBody);
        } else {
          // receiving string response
          return decryptedBody;
        }
      } else {
        return event;
      }
    } catch (error) {
      return event;
    }
  }
}
